import dayjs from "dayjs";
import { resolvePeriod } from "@/ui/components/date/dateRangeValidation";
import { INLINE_FILE_NAME } from "./useFilterDraft";
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;

export type SegmentKind =
	| "plain"
	| "dimension"
	| "scope"
	| "depth"
	| "yield"
	| "value"
	| "exclusion"
	| "polarity"
	| "prompt";

export type Segment = {
	text: string;
	kind: SegmentKind;
};

export type Translator = (key: string) => string;

export type DescribeOptions = {
	valueLimit?: number;
	exclusionLimit?: number;
	depthYield?: number | null;
	totalValues?: number | null;
};

type Slots = Record<string, Segment[]>;

const seg = (text: string, kind: SegmentKind = "plain"): Segment => ({
	text,
	kind,
});

const fill = (template: string, slots: Slots): Segment[] => {
	const parts = template.split(/(\{[a-zA-Z]+\})/);
	const out: Segment[] = [];

	for (const part of parts) {
		if (part === "") {
			continue;
		}

		const placeholder = part.match(/^\{([a-zA-Z]+)\}$/);

		if (!placeholder) {
			out.push(seg(part));
			continue;
		}

		out.push(...(slots[placeholder[1]] ?? []));
	}

	return out;
};

const countOf = (
	t: Translator,
	count: number,
	singularKey: string,
	pluralKey: string
): string =>
	(count === 1 ? t(singularKey) : t(pluralKey)).replace(
		"{count}",
		String(count)
	);

const listOf = (
	t: Translator,
	labels: string[],
	limit: number,
	kind: SegmentKind
): Segment[] => {
	if (labels.length <= limit) {
		return labels.flatMap((label, index) =>
			index === 0 ? [seg(label, kind)] : [seg(", "), seg(label, kind)]
		);
	}

	const named = labels.slice(0, limit);
	const remaining = labels.length - limit;

	return fill(t("filterDialog.summary.overflow"), {
		named: named.flatMap((label, index) =>
			index === 0 ? [seg(label, kind)] : [seg(", "), seg(label, kind)]
		),
		count: [seg(String(remaining), "yield")],
	});
};

const labelsOf = (values: FilterValueDTO[] | null | undefined): string[] =>
	(values ?? [])
		.map((value) => value.label)
		.filter((label): label is string => Boolean(label));

const dimensionTitle = (
	t: Translator,
	filter: Partial<ActiveFilterDTO>
): string | null => {
	const nameClean = filter.dimension?.name_clean;

	return nameClean ? t(`properties.${nameClean}.title`) : null;
};

const describeHierarchy = (
	t: Translator,
	value: FilterValueDTO,
	options: DescribeOptions
): Segment[] => {
	const exclusionLimit = options.exclusionLimit ?? 3;

	const exclusions = (value.excluded_branches ?? []).filter(
		(branch) => value.level === null || branch.level < value.level
	);

	const depth = countOf(
		t,
		value.selection_depth ?? 1,
		"filterDialog.unit.level",
		"filterDialog.unit.levels"
	);

	const hasYield =
		options.depthYield !== null && options.depthYield !== undefined;

	const slots: Slots = {
		scope: [seg(value.label ?? "", "scope")],
		depth: [seg(depth, "depth")],
		exclusions: listOf(
			t,
			exclusions.map((branch) => branch.label),
			exclusionLimit,
			"exclusion"
		),
	};

	if (hasYield) {
		slots.yield = [
			seg(
				countOf(
					t,
					options.depthYield as number,
					"filterDialog.unit.category",
					"filterDialog.unit.categories"
				),
				"yield"
			),
		];
	}

	const key = hasYield
		? exclusions.length > 0
			? "filterDialog.summary.hierarchyExcluding"
			: "filterDialog.summary.hierarchy"
		: exclusions.length > 0
		? "filterDialog.summary.hierarchyNoYieldExcluding"
		: "filterDialog.summary.hierarchyNoYield";

	return fill(t(key), slots);
};

const describeFile = (t: Translator, value: FilterValueDTO): Segment[] => {
	const file = value.file;

	if (file?.name && file.name !== INLINE_FILE_NAME) {
		return fill(t("filterDialog.summary.file"), {
			name: [seg(file.name, "value")],
		});
	}

	const lines = (file?.data ?? "")
		.split("\n")
		.map((line) => line.trim())
		.filter(Boolean).length;

	// The backend keeps no copy of a pasted list over 10.000 characters, so
	// there is nothing left to count.
	if (lines === 0) {
		return fill(t("filterDialog.summary.fileInlineUnknown"), {});
	}

	return fill(t("filterDialog.summary.fileInline"), {
		count: [
			seg(
				countOf(
					t,
					lines,
					"filterDialog.unit.value",
					"filterDialog.unit.values"
				),
				"yield"
			),
		],
	});
};

const DATE_FORMAT = "D MMM YYYY";

const describePeriod = (t: Translator, value: FilterValueDTO): Segment[] => {
	const period = value.filter_period
		? resolvePeriod(value.filter_period)
		: value.filter_period;

	// dayjs(undefined) is today and reports itself as valid, so an unset range
	// has to be caught before it is parsed.
	if (!period?.from_parsed || !period?.to_parsed) {
		return [seg(t("filterDialog.summary.nothingSelected"), "prompt")];
	}

	const from = dayjs(period.from_parsed);
	const to = dayjs(period.to_parsed);

	if (!from.isValid() || !to.isValid()) {
		return [seg(t("filterDialog.summary.nothingSelected"), "prompt")];
	}

	if (to.diff(from, "day") < 1) {
		return fill(t("filterDialog.summary.periodDay"), {
			day: [seg(from.format(DATE_FORMAT), "value")],
		});
	}

	return fill(t("filterDialog.summary.period"), {
		from: [seg(from.format(DATE_FORMAT), "value")],
		to: [seg(to.format(DATE_FORMAT), "value")],
	});
};

const describeValues = (
	t: Translator,
	filter: Partial<ActiveFilterDTO>,
	options: DescribeOptions
): Segment[] => {
	const valueLimit = options.valueLimit ?? 3;
	const labels = labelsOf(filter.values);

	if (labels.length === 0) {
		return [seg(t("filterDialog.summary.nothingSelected"), "prompt")];
	}

	const values = listOf(t, labels, valueLimit, "value");

	if (filter.valuesAreIncluded === false) {
		return fill(t("filterDialog.summary.excluded"), { values });
	}

	const total = options.totalValues;

	if (total && labels.length < total) {
		return fill(t("filterDialog.summary.includedOfTotal"), {
			selected: [seg(String(labels.length), "yield")],
			total: [
				seg(
					countOf(
						t,
						total,
						"filterDialog.unit.value",
						"filterDialog.unit.values"
					),
					"yield"
				),
			],
			values,
		});
	}

	return fill(t("filterDialog.summary.included"), { values });
};

export const describeFilter = (
	filter: Partial<ActiveFilterDTO> | null | undefined,
	t: Translator,
	options: DescribeOptions = {}
): Segment[] => {
	const title = filter ? dimensionTitle(t, filter) : null;

	if (!filter || !title) {
		return [seg(t("filterDialog.summary.empty"), "prompt")];
	}

	const value = filter.values?.[0];
	const isHierarchy = Boolean(filter.dimension?.hierarchy_definition_id);

	let body: Segment[];

	if (isHierarchy && value) {
		body = describeHierarchy(t, value, options);
	} else if (value?.file) {
		body = describeFile(t, value);
	} else if (value?.filter_period) {
		body = describePeriod(t, value);
	} else {
		body = describeValues(t, filter, options);
	}

	if (filter.isApplied === false) {
		body = fill(t("filterDialog.summary.inactive"), { body });
	}

	return fill(t("filterDialog.summary.line"), {
		dimension: [seg(title, "dimension")],
		body,
	});
};

export const describeFilterAsText = (segments: Segment[]): string =>
	segments.map((segment) => segment.text).join("");
